// Whether an auth route renders `<shortlist-header>`, and how.
//
// ⚠️ IT IS NOT UNIFORM, AND THE PORT ASSUMED IT WAS. `AuthLayout` rendered
// `<ShortlistHeader hideDynamicCtas />` once for every auth route, on the reasoning that the
// abstract state is the natural home for chrome every child shares. Upstream does the
// opposite: the element is written into each state's own `template`, and the twelve states
// disagree four ways. Seven of them do not render it at all.
//
// This was invisible until the baseline could capture a SIGNED-IN screen. `/auth/status` is
// the first route where upstream's condition evaluates differently from ours, and the diff
// came back at 8.69% — a 32px band at the top plus everything below it displaced. Every
// signed-out screen we had a reference for happens to be one of the states that renders the
// header unconditionally, so the assumption held everywhere it could be checked.
//
// Transcribed from the `.state()` call sites, which are the only statement of this that
// exists (`routes.json` carries `data`, not templates):
//
//   authentication/login/login.state.config.ng1.ts:10            <shortlist-header hide-dynamic-ctas="::true">
//   authentication/forgot-password/…:10                          <shortlist-header hide-dynamic-ctas="::true">
//   authentication/sign-up/…:10                                  <shortlist-header hide-dynamic-ctas="::true">
//   authentication/user/…:12                                     <shortlist-header>          (no hide-dynamic-ctas)
//   authentication/status/…:14                                   ng-if="::(!stateCtrl.hideHeader)"
//   authentication/multi-factor-auth/…:13                        ng-if="::(!mfaCtrl.hideHeader)"
//   authentication/enable-cookies/…:10                           <!-- commented out -->
//   authentication/out-of-date/…:10                              <!-- commented out -->
//   authentication/reset-password/…:10                           <!-- commented out -->
//   authentication/sso/callback/…:10                             <!-- commented out -->
//   authentication/verify/…:10                                   <!-- commented out -->
//   authentication/multi-factor-auth/reset-request/…:10          <!-- commented out -->
//   authentication/multi-factor-auth/validate/…:11               <!-- commented out -->
//
// and in both conditional cases the controller computes
// `hideHeader = !extensionFrameService.isInIframe()` — so the header is for the framed
// extension, and a top-level tab shows none. The commented-out ones are left in place
// upstream rather than deleted; they are still "never renders" and are ported as such, not
// as an invitation to switch them on.
//
// ⚠️ THE MFA CHILDREN INHERIT. `/auth/mfa/validate` and `/auth/mfa/reset-request` have their
// own header commented out, but their ABSTRACT parent renders one in-iframe, and a child's
// template is nested inside the parent's. So the effective policy for both is the parent's,
// not `never` — which is why this is keyed by longest-prefix and the children are absent
// below rather than listed as `never`.

/** How a route treats the header. `in-iframe` is upstream's `!isInIframe()` condition. */
export type HeaderPolicy = 'always' | 'in-iframe' | 'never'

export interface HeaderRule {
  policy: HeaderPolicy
  /** Upstream's `hide-dynamic-ctas="::true"`. Only the four unconditional states set it. */
  hideDynamicCtas: boolean
}

const RULES: ReadonlyArray<readonly [string, HeaderRule]> = [
  ['/chrome-extension/auth/login', { policy: 'always', hideDynamicCtas: true }],
  ['/chrome-extension/auth/forgot-password', { policy: 'always', hideDynamicCtas: true }],
  ['/chrome-extension/auth/sign-up', { policy: 'always', hideDynamicCtas: true }],
  ['/chrome-extension/auth/user', { policy: 'always', hideDynamicCtas: false }],
  ['/chrome-extension/auth/status', { policy: 'in-iframe', hideDynamicCtas: false }],
  // The abstract parent, matched by prefix so both children inherit it — see the note above.
  ['/chrome-extension/auth/mfa', { policy: 'in-iframe', hideDynamicCtas: false }],
  ['/chrome-extension/auth/enable-cookies', { policy: 'never', hideDynamicCtas: false }],
  ['/chrome-extension/auth/out-of-date', { policy: 'never', hideDynamicCtas: false }],
  ['/chrome-extension/auth/reset-password', { policy: 'never', hideDynamicCtas: false }],
  ['/chrome-extension/auth/sso/callback', { policy: 'never', hideDynamicCtas: false }],
  ['/chrome-extension/auth/verify', { policy: 'never', hideDynamicCtas: false }],
]

/**
 * The rule for a pathname, longest prefix first.
 *
 * Unknown paths get `never`. That is the conservative direction on purpose: a route added
 * upstream and not transcribed here renders no chrome rather than silently inheriting
 * someone else's, and `authHeader.test.ts` fails on it so it does not stay that way.
 */
export function headerRuleFor(pathname: string): HeaderRule {
  const match = [...RULES]
    .filter(([path]) => pathname === path || pathname.startsWith(`${path}/`))
    .sort(([a], [b]) => b.length - a.length)[0]
  return match?.[1] ?? { policy: 'never', hideDynamicCtas: false }
}

/** Whether to render it, given where this document is running. */
export function showsHeader(pathname: string, inIframe: boolean): boolean {
  const { policy } = headerRuleFor(pathname)
  return policy === 'always' || (policy === 'in-iframe' && inIframe)
}

/** Every path this table states a rule for — the parity test's input. */
export const HEADER_RULE_PATHS: readonly string[] = RULES.map(([path]) => path)
